import React from 'react';

const JsonDiff = ({ oldData, newData, title = "Cart Changes", isExpanded = false }) => {
  const getDiff = (oldObj, newObj, path = '') => {
    const changes = [];
    
    // Process regular items
    const oldItems = oldObj?.items || [];
    const newItems = newObj?.items || [];
    
    const oldItemsMap = new Map();
    oldItems.forEach((item, index) => {
      const key = `${item.name}-${item.option || 'default'}`;
      oldItemsMap.set(key, { ...item, index });
    });
    
    const newItemsMap = new Map();
    newItems.forEach((item, index) => {
      const key = `${item.name}-${item.option || 'default'}`;
      newItemsMap.set(key, { ...item, index });
    });
    
    // First, check for items that changed options (same name, different option)
    const processedItems = new Set();
    
    oldItems.forEach((oldItem, oldIndex) => {
      // Find if there's a matching item by name only in the new items
      const newItemIndex = newItems.findIndex(newItem => 
        newItem.name === oldItem.name && 
        !processedItems.has(newItem.name)
      );
      
      if (newItemIndex !== -1) {
        const newItem = newItems[newItemIndex];
        
        // Check if this is actually a modification (option or addons changed)
        if (oldItem.option !== newItem.option || 
            JSON.stringify(oldItem.addons || []) !== JSON.stringify(newItem.addons || [])) {
          processedItems.add(newItem.name);
          
          // Determine what changed
          const changedParts = [];
          if (oldItem.option !== newItem.option) {
            changedParts.push(`size: ${oldItem.option} → ${newItem.option}`);
          }
          
          const normalizeAddon = (addon) => {
            if (typeof addon === 'string') return addon;
            if (typeof addon === 'object' && addon !== null) return addon.name || JSON.stringify(addon);
            return String(addon);
          };
          
          const oldAddons = new Set((oldItem.addons || []).map(normalizeAddon));
          const newAddons = new Set((newItem.addons || []).map(normalizeAddon));
          
          const addedAddons = [...newAddons].filter(a => !oldAddons.has(a));
          const removedAddons = [...oldAddons].filter(a => !newAddons.has(a));
          
          if (addedAddons.length > 0 || removedAddons.length > 0) {
            changedParts.push('toppings modified');
          }
          
          changes.push({
            type: 'modified',
            path: `items[${newItemIndex}]`,
            oldValue: oldItem,
            newValue: newItem,
            description: `${newItem.name} ${changedParts.join(', ')}`,
            addedAddons,
            removedAddons
          });
        }
      }
    });
    
    oldItemsMap.forEach((oldItem, key) => {
      if (!newItemsMap.has(key) && !processedItems.has(oldItem.name)) {
        changes.push({
          type: 'removed',
          path: `items[${oldItem.index}]`,
          oldValue: oldItem,
          newValue: null,
          description: `Removed ${oldItem.name}${oldItem.option ? ` (${oldItem.option})` : ''}`
        });
      } else if (newItemsMap.has(key) && !processedItems.has(oldItem.name)) {
        // Only process if this item wasn't already handled as a modification
        const newItem = newItemsMap.get(key);
        
        if (oldItem.quantity !== newItem.quantity) {
          changes.push({
            type: 'modified',
            path: `items[${newItem.index}].quantity`,
            oldValue: oldItem.quantity,
            newValue: newItem.quantity,
            description: `${newItem.name} quantity: ${oldItem.quantity} → ${newItem.quantity}`
          });
        }
        
        const normalizeAddon = (addon) => {
          if (typeof addon === 'string') return addon;
          if (typeof addon === 'object' && addon !== null) return addon.name || JSON.stringify(addon);
          return String(addon);
        };
        
        const oldAddons = new Set((oldItem.addons || []).map(normalizeAddon));
        const newAddons = new Set((newItem.addons || []).map(normalizeAddon));
        
        const addedAddons = [...newAddons].filter(a => !oldAddons.has(a));
        const removedAddons = [...oldAddons].filter(a => !newAddons.has(a));
        
        if (addedAddons.length > 0 || removedAddons.length > 0) {
          changes.push({
            type: 'modified',
            path: `items[${newItem.index}].addons`,
            oldValue: oldItem.addons || [],
            newValue: newItem.addons || [],
            description: `${newItem.name} addons changed`,
            addedAddons,
            removedAddons
          });
        }
      }
    });
    
    newItemsMap.forEach((newItem, key) => {
      if (!oldItemsMap.has(key) && !processedItems.has(newItem.name)) {
        changes.push({
          type: 'added',
          path: `items[${newItem.index}]`,
          oldValue: null,
          newValue: newItem,
          description: `Added ${newItem.name}${newItem.option ? ` (${newItem.option})` : ''}`
        });
      }
    });
    
    // Process combos
    const oldCombos = oldObj?.combos || [];
    const newCombos = newObj?.combos || [];
    
    const oldCombosMap = new Map();
    oldCombos.forEach((combo, index) => {
      const key = combo.name;
      oldCombosMap.set(key, { ...combo, index });
    });
    
    const newCombosMap = new Map();
    newCombos.forEach((combo, index) => {
      const key = combo.name;
      newCombosMap.set(key, { ...combo, index });
    });
    
    // Check for removed combos
    oldCombosMap.forEach((oldCombo, key) => {
      if (!newCombosMap.has(key)) {
        changes.push({
          type: 'removed',
          path: `combos[${oldCombo.index}]`,
          oldValue: oldCombo,
          newValue: null,
          description: `Removed ${oldCombo.name} combo`
        });
      } else {
        const newCombo = newCombosMap.get(key);
        
        // Check quantity change
        if (oldCombo.quantity !== newCombo.quantity) {
          changes.push({
            type: 'modified',
            path: `combos[${newCombo.index}].quantity`,
            oldValue: oldCombo.quantity,
            newValue: newCombo.quantity,
            description: `${newCombo.name} combo quantity: ${oldCombo.quantity} → ${newCombo.quantity}`
          });
        }
        
        // Check for changes in combo items
        const oldComboItems = oldCombo.items || [];
        const newComboItems = newCombo.items || [];
        
        if (oldComboItems.length !== newComboItems.length) {
          // Create a more detailed description of what changed
          const addedItems = newComboItems.filter(item => 
            !oldComboItems.some(oldItem => oldItem.name === item.name)
          );
          const removedItems = oldComboItems.filter(item => 
            !newComboItems.some(newItem => newItem.name === item.name)
          );
          
          // Instead of a single modified change, add individual changes for each added/removed item
          if (addedItems.length > 0 || removedItems.length > 0) {
            // Add the combo header change
            changes.push({
              type: 'modified',
              path: `combos[${newCombo.index}].items`,
              oldValue: oldComboItems,
              newValue: newComboItems,
              description: `${newCombo.name} combo items changed`,
              isComboHeader: true
            });
            
            // Add individual changes for each added item
            addedItems.forEach(item => {
              changes.push({
                type: 'combo_item_added',
                path: `combos[${newCombo.index}].items`,
                newValue: item,
                comboName: newCombo.name,
                description: `Added to ${newCombo.name} combo`
              });
            });
            
            // Add individual changes for each removed item
            removedItems.forEach(item => {
              changes.push({
                type: 'combo_item_removed',
                path: `combos[${newCombo.index}].items`,
                oldValue: item,
                comboName: newCombo.name,
                description: `Removed from ${newCombo.name} combo`
              });
            });
          } else {
            // Just reordered or modified existing items
            changes.push({
              type: 'modified',
              path: `combos[${newCombo.index}].items`,
              oldValue: oldComboItems,
              newValue: newComboItems,
              description: `${newCombo.name} combo items reordered`
            });
          }
        } else {
          // Check for modifications in combo items
          for (let i = 0; i < newComboItems.length; i++) {
            const oldItem = oldComboItems[i];
            const newItem = newComboItems[i];
            
            if (!oldItem || !newItem) continue;
            
            if (oldItem.name !== newItem.name || 
                oldItem.option !== newItem.option ||
                JSON.stringify(oldItem.addons) !== JSON.stringify(newItem.addons)) {
              changes.push({
                type: 'modified',
                path: `combos[${newCombo.index}].items[${i}]`,
                oldValue: oldItem,
                newValue: newItem,
                description: oldItem.name !== newItem.name 
              ? `${newCombo.name} combo item changed: ${oldItem.name} → ${newItem.name}`
              : oldItem.option !== newItem.option
              ? `${newCombo.name} combo: ${newItem.name} option changed from ${oldItem.option} to ${newItem.option}`
              : `${newCombo.name} combo: ${newItem.name} toppings changed`
              });
            }
          }
        }
      }
    });
    
    // Check for added combos
    newCombosMap.forEach((newCombo, key) => {
      if (!oldCombosMap.has(key)) {
        changes.push({
          type: 'added',
          path: `combos[${newCombo.index}]`,
          oldValue: null,
          newValue: newCombo,
          description: `Added ${newCombo.name} combo`
        });
      }
    });
    
    const oldTotal = typeof oldObj?.total === 'number' ? oldObj.total : parseFloat(oldObj?.total) || 0;
    const newTotal = typeof newObj?.total === 'number' ? newObj.total : parseFloat(newObj?.total) || 0;
    
    if (oldTotal !== newTotal) {
      changes.push({
        type: 'modified',
        path: 'total',
        oldValue: oldTotal,
        newValue: newTotal,
        description: `Total: $${oldTotal.toFixed(2)} → $${newTotal.toFixed(2)}`
      });
    }
    
    return changes;
  };

  const changes = getDiff(oldData, newData);
  
  if (changes.length === 0) {
    return null;
  }

  // Generate a compact summary for collapsed state
  const getSummary = () => {
    const highlights = [];
    
    // Look for the most important changes
    changes.forEach(change => {
      // Show specific combo changes
      if (change.path.includes('combos') && change.type === 'added' && change.newValue?.name) {
        highlights.push(`Added ${change.newValue.name}`);
      } else if (change.path.includes('combos') && change.type === 'removed' && change.oldValue?.name) {
        highlights.push(`Removed ${change.oldValue.name}`);
      } else if (change.description && change.description.includes('combo items changed')) {
        // This is our special combo items change
        highlights.push(change.description);
      } else if (change.path.includes('items') && !change.path.includes('combos')) {
        // Regular item changes
        if (change.type === 'added' && change.newValue?.name) {
          highlights.push(`Added ${change.newValue.name}`);
        } else if (change.type === 'removed' && change.oldValue?.name) {
          highlights.push(`Removed ${change.oldValue.name}`);
        }
      }
    });
    
    // If we have specific highlights, show those; otherwise show counts
    if (highlights.length > 0) {
      return highlights.slice(0, 3).join(' • ') + (highlights.length > 3 ? ` • +${highlights.length - 3} more` : '');
    }
    
    // Fallback to counts
    const summaryParts = [];
    const addedCount = changes.filter(c => c.type === 'added').length;
    const removedCount = changes.filter(c => c.type === 'removed').length;
    const modifiedCount = changes.filter(c => c.type === 'modified').length;
    
    if (addedCount > 0) summaryParts.push(`${addedCount} added`);
    if (removedCount > 0) summaryParts.push(`${removedCount} removed`);
    if (modifiedCount > 0) summaryParts.push(`${modifiedCount} modified`);
    
    return summaryParts.join(', ') || 'Cart updated';
  };

  // Render component - always show the colored changes, but without Previous/New sections when collapsed
  return (
    <div className="bg-gray-900 rounded-lg p-4 mb-4 text-sm hover:bg-gray-900/80 transition-colors cursor-pointer">
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <h3 className="text-cyan-400 font-semibold">{title}</h3>
      </div>
      
      <div className="space-y-2">
        {changes.map((change, index) => {
          // Skip the combo header if we're in collapsed mode - we'll show individual items instead
          if (!isExpanded && change.isComboHeader) {
            return null;
          }
          
          return (
          <div
            key={index}
            className={`px-3 py-2 rounded border ${
              change.type === 'added' || change.type === 'combo_item_added'
                ? 'bg-green-900/20 border-green-700/50 text-green-400'
                : change.type === 'removed' || change.type === 'combo_item_removed'
                ? 'bg-red-900/20 border-red-700/50 text-red-400'
                : 'bg-yellow-900/20 border-yellow-700/50 text-yellow-400'
            }`}
          >
            <div className="flex items-start gap-2">
              <span className="font-mono text-xs opacity-70">
                {(change.type === 'added' || change.type === 'combo_item_added') && '+'}
                {(change.type === 'removed' || change.type === 'combo_item_removed') && '-'}
                {change.type === 'modified' && '~'}
              </span>
              <div className="flex-1">
                <div className="font-medium">{change.description}</div>
                
                {/* Handle combo item additions */}
                {change.type === 'combo_item_added' && change.newValue && (
                  <div className="mt-1 text-xs opacity-80">
                    <div>
                      {change.newValue.name}
                      {change.newValue.option && <span> ({change.newValue.option})</span>}
                      {change.newValue.quantity && change.newValue.quantity > 1 && (
                        <span> × {change.newValue.quantity}</span>
                      )}
                    </div>
                    {change.newValue.addons && change.newValue.addons.length > 0 && (
                      <div className="mt-1 pl-2">
                        <span>with {change.newValue.addons.map(addon => 
                          typeof addon === 'string' ? addon : (addon.name || JSON.stringify(addon))
                        ).join(', ')}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Handle combo item removals */}
                {change.type === 'combo_item_removed' && change.oldValue && (
                  <div className="mt-1 text-xs opacity-80 line-through">
                    <div>
                      {change.oldValue.name}
                      {change.oldValue.option && <span> ({change.oldValue.option})</span>}
                      {change.oldValue.quantity && change.oldValue.quantity > 1 && (
                        <span> × {change.oldValue.quantity}</span>
                      )}
                    </div>
                    {change.oldValue.addons && change.oldValue.addons.length > 0 && (
                      <div className="mt-1 pl-2">
                        <span>with {change.oldValue.addons.map(addon => 
                          typeof addon === 'string' ? addon : (addon.name || JSON.stringify(addon))
                        ).join(', ')}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {change.type === 'added' && change.newValue && (
                  <div className="mt-1 text-xs opacity-80">
                    <div>
                      {change.newValue.quantity}x
                      {change.newValue.price != null && (
                        <span> @ ${(typeof change.newValue.price === 'number' ? change.newValue.price : parseFloat(change.newValue.price) || 0).toFixed(2)}</span>
                      )}
                    </div>
                    {/* Handle combo items */}
                    {change.path.includes('combos') && change.newValue.items && change.newValue.items.length > 0 && (
                      <div className="mt-2 pl-2 border-l-2 border-green-700/50">
                        <div className="text-green-300 font-medium mb-1">Combo includes:</div>
                        {change.newValue.items.map((item, idx) => (
                          <div key={idx} className="pl-2 py-0.5">
                            <span className="text-green-400">• </span>
                            <span>{item.name}</span>
                            {item.option && <span className="text-green-300"> ({item.option})</span>}
                            {item.addons && item.addons.length > 0 && (
                              <span className="text-green-300"> with {item.addons.map(addon => 
                                typeof addon === 'string' ? addon : (addon.name || JSON.stringify(addon))
                              ).join(', ')}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Handle regular item addons */}
                    {!change.path.includes('combos') && change.newValue.addons && change.newValue.addons.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {change.newValue.addons.map((addon, idx) => (
                          <div key={idx} className="flex items-center gap-1 pl-2">
                            <span className="text-green-400">+</span>
                            <span>{typeof addon === 'string' ? addon : (addon.name || JSON.stringify(addon))}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {change.type === 'removed' && change.oldValue && (
                  <div className="mt-1 text-xs opacity-80">
                    <div className="line-through">
                      {change.oldValue.quantity}x
                      {change.oldValue.price != null && (
                        <span> @ ${(typeof change.oldValue.price === 'number' ? change.oldValue.price : parseFloat(change.oldValue.price) || 0).toFixed(2)}</span>
                      )}
                    </div>
                    {/* Handle combo items */}
                    {change.path.includes('combos') && change.oldValue.items && change.oldValue.items.length > 0 && (
                      <div className="mt-2 pl-2 border-l-2 border-red-700/50 line-through">
                        <div className="text-red-300 font-medium mb-1">Combo included:</div>
                        {change.oldValue.items.map((item, idx) => (
                          <div key={idx} className="pl-2 py-0.5">
                            <span className="text-red-400">• </span>
                            <span>{item.name}</span>
                            {item.option && <span className="text-red-300"> ({item.option})</span>}
                            {item.addons && item.addons.length > 0 && (
                              <span className="text-red-300"> with {item.addons.map(addon => 
                                typeof addon === 'string' ? addon : (addon.name || JSON.stringify(addon))
                              ).join(', ')}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Handle regular item addons */}
                    {!change.path.includes('combos') && change.oldValue.addons && change.oldValue.addons.length > 0 && (
                      <div className="mt-1 space-y-0.5 line-through">
                        {change.oldValue.addons.map((addon, idx) => (
                          <div key={idx} className="flex items-center gap-1 pl-2">
                            <span className="text-red-400">-</span>
                            <span>{typeof addon === 'string' ? addon : (addon.name || JSON.stringify(addon))}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {change.type === 'modified' && change.addedAddons && (
                  <div className="mt-2 space-y-1">
                    {change.addedAddons.length > 0 && (
                      <div className="space-y-1">
                        {change.addedAddons.map((addon, idx) => (
                          <div key={`add-${idx}`} className="flex items-center gap-2 text-green-400">
                            <span className="font-mono text-xs">+</span>
                            <span className="text-sm">Added topping: {addon}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {change.removedAddons.length > 0 && (
                      <div className="space-y-1">
                        {change.removedAddons.map((addon, idx) => (
                          <div key={`rem-${idx}`} className="flex items-center gap-2 text-red-400">
                            <span className="font-mono text-xs">-</span>
                            <span className="text-sm">Removed topping: {addon}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Show detailed combo changes ONLY when expanded */}
                {isExpanded && change.type === 'modified' && change.path.includes('combos') && change.path.includes('.items') && !change.path.includes('.items[') && (
                  <div className="mt-2 text-xs opacity-80">
                    {change.oldValue && change.newValue && (
                      <div className="space-y-2">
                        <div className="pl-2 border-l-2 border-yellow-700/50">
                          <div className="text-yellow-300 font-medium mb-1">Previous combo items:</div>
                          {change.oldValue.map((item, idx) => (
                            <div key={`old-${idx}`} className="pl-2 py-0.5 text-yellow-400/70">
                              <span>• </span>
                              <span>{item.name}</span>
                              {item.option && <span> ({item.option})</span>}
                              {item.addons && item.addons.length > 0 && (
                                <span> with {item.addons.map(addon => 
                                  typeof addon === 'string' ? addon : (addon.name || JSON.stringify(addon))
                                ).join(', ')}</span>
                              )}
                            </div>
                          ))}
                        </div>
                        <div className="pl-2 border-l-2 border-yellow-700/50">
                          <div className="text-yellow-300 font-medium mb-1">New combo items:</div>
                          {change.newValue.map((item, idx) => (
                            <div key={`new-${idx}`} className="pl-2 py-0.5">
                              <span className="text-yellow-400">• </span>
                              <span>{item.name}</span>
                              {item.option && <span className="text-yellow-300"> ({item.option})</span>}
                              {item.addons && item.addons.length > 0 && (
                                <span className="text-yellow-300"> with {item.addons.map(addon => 
                                  typeof addon === 'string' ? addon : (addon.name || JSON.stringify(addon))
                                ).join(', ')}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default JsonDiff; 
